import React, { useRef, useState, useEffect, createContext } from 'react';
import isEqual from 'lodash/isEqual';
import type { ActorFilterProps } from '../../common/types';
import type { Props, TableResetContext } from './types';

export const defaultReset: TableResetContext = {
	isPageReset: false,
	isFiltersApplied: false,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	togglePageReset: () => {},
};

const TableResetterContext = createContext<TableResetContext>(defaultReset);

const shouldPageBeReset = (
	prevFilters: ActorFilterProps,
	currentFilters: ActorFilterProps,
): boolean => {
	// If pageNumber or pageSize is changed, then we don't need to reset otherwise reset always
	const { pageNumber: prevPageNumber, pageSize: prevPageSize, ...prevRestFilters } = prevFilters;
	const {
		pageNumber: currentPageNumber,
		pageSize: currentPageSize,
		...currentRestFilters
	} = currentFilters;

	return !isEqual(prevRestFilters, currentRestFilters);
};

const hasFiltersApplied = (
	currentFilters: ActorFilterProps,
	defaultFilters: ActorFilterProps,
): boolean => !isEqual(currentFilters, defaultFilters);
export const TableResetter = (props: Props) => {
	const { filters, defaultFilters, children } = props;
	const previousFilters = useRef<ActorFilterProps>(filters);
	const [isPageReset, setPageReset] = useState<boolean>(false);
	const [isFiltersApplied, setFiltersApplied] = useState<boolean>(false);

	useEffect(() => {
		const hasPageReset: boolean = shouldPageBeReset(previousFilters.current, filters);
		const hasFilterApplied: boolean = hasFiltersApplied(filters, defaultFilters);

		previousFilters.current = filters;
		setPageReset(hasPageReset);
		setFiltersApplied(hasFilterApplied);
	}, [filters, defaultFilters]);

	return (
		<TableResetterContext.Provider
			value={{
				isPageReset,
				isFiltersApplied,
				togglePageReset: setPageReset,
			}}
		>
			{children}
		</TableResetterContext.Provider>
	);
};

export const TableResetterConsumer = TableResetterContext.Consumer;

export default TableResetterContext;
